import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import EmployeeFormFields from '../components/EmployeeFormFields';
import AssetManagement from '../components/AssetManagement';
import SuperAdminPortal from '../components/SuperAdminPortal';
import EmployeeAnalytics from '../components/EmployeeAnalytics';
import TrainingManagement from '../components/TrainingManagement';
import HRPortal from '../components/HRPortal';
import HiringPortal from '../components/HiringPortal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Calendar, FileText, DollarSign, Plus, Edit, Trash2, Target, RefreshCw, ClipboardCheck, ClipboardList, Clock, AlertTriangle, Bell, Power, CalendarDays, Settings, Mail, AlertCircle, Package, Shield, BarChart3, BookOpen, Download, Upload, FileSpreadsheet, Eye, UserPlus, IndianRupee, Wallet, Search, SlidersHorizontal, Rocket, ArrowRightLeft, Activity, Megaphone, HandCoins } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import PnLPortal from '../components/PnLPortal';
import PayrollPortal from '../components/PayrollPortal';
import PortalAccessManager from '../components/PortalAccessManager';
import KPPManagement from '../components/KPPManagement';
import AppraisalKPPManagement from '../components/AppraisalKPPManagement';
import AppraisalReportsAdmin from '../components/AppraisalReportsAdmin';
import CompensationManagement from '../components/CompensationManagement';
import DeadlineMonitor from '../components/DeadlineMonitor';
import SecuritySettings from '../components/SecuritySettings';
import VersionPublisher from '../components/VersionPublisher';
import ManagerHistoryTransfer from '../components/ManagerHistoryTransfer';
import APMPortal from '../components/APMPortal';
import BannerManager from '../components/BannerManager';
import TeamAttendanceView from '../components/TeamAttendanceView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getErrorMessage = (error, fallback = 'Operation failed') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return fallback;
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split(' ')[0].split('T')[0];
};

const AdminDashboard = ({ user, setUser }) => {
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  // Bulk Upload State
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Employee Table Search & Column Visibility
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    employee_id: true,
    name: true,
    email: true,
    role: true,
    department: true,
    designation: false,
    date_of_joining: false,
    employee_type: false,
    level: false,
    contract_end_date: false,
    internship_end_date: false,
    status: true,
    actions: true,
  });

  const columnDefs = [
    { key: 'employee_id', label: 'Employee ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'department', label: 'Department' },
    { key: 'designation', label: 'Designation' },
    { key: 'date_of_joining', label: 'Date of Joining' },
    { key: 'employee_type', label: 'Employee Type' },
    { key: 'level', label: 'Level' },
    { key: 'contract_end_date', label: 'Contract End Date' },
    { key: 'internship_end_date', label: 'Internship End Date' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ];

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch.trim()) return employees;
    const q = employeeSearch.toLowerCase();
    return employees.filter(emp =>
      (emp.employee_id || '').toLowerCase().includes(q) ||
      (emp.name || '').toLowerCase().includes(q) ||
      (emp.email || '').toLowerCase().includes(q) ||
      (emp.role || '').toLowerCase().includes(q) ||
      (emp.department || '').toLowerCase().includes(q) ||
      (emp.designation || '').toLowerCase().includes(q)
    );
  }, [employees, employeeSearch]);
  
  // Deadline Settings State

  


  const [employeeForm, setEmployeeForm] = useState({
    employee_id: '',
    name: '',
    email: '',
    password: '',
    role: 'employee',
    manager_id: '',
    date_of_joining: '',
    department: '',
    designation: '',
    // Personal Details
    gender: '',
    dob: '',
    marital_status: '',
    nationality: '',
    present_address: '',
    permanent_address: '',
    contact_number: '',
    alternate_number: '',
    alternate_email: '',
    education: '',
    previous_employment: '',
    physically_disabled: 'No',
    // Employment Details
    employee_type: '',
    level: '',
    reporting_manager: '',
    rm_email: '',
    hr_partner: '',
    hr_email: '',
    converted_employee: false,
    conversion_date: '',
    actual_date_of_joining: '',
    company_asset: '',
    serial_number: '',
    total_work_experience: '',
    work_experience_as_employee: '',
    pan_number: '',
    aadhar_number: '',
    bank_details: '',
    project_name: '',
    last_promotion: '',
    contract_end_date: '',
    contract_period: '',
    internship_end_date: '',
    internship_period: '',
    profile_photo: '',
    payslip_folder_id: '',
    // Salary Structure
    salary_currency: '',
    fixed_monthly_salary: '',
    internet_phone_allowance: '',
    monthly_performance_bonus: '',
    byod_allowance: '',
    half_yearly_retention_bonus: '',
    managerial_allowance: '',
    provident_fund: '',
    medical_allowance: '',
    gratuity: '',
    hra: '',
    ctc_yearly: ''
  });

  const [holidayForm, setHolidayForm] = useState({
    date: '',
    name: '',
    holiday_type: 'organization'
  });
  const [editingHoliday, setEditingHoliday] = useState(null);

  const [policyForm, setPolicyForm] = useState({
    title: '',
    drive_file_id: ''
  });

  const [editingPolicy, setEditingPolicy] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [employeesRes, holidaysRes, policiesRes] = await Promise.all([
        axios.get(`${API}/users`, config),
        axios.get(`${API}/holidays`, config),
        axios.get(`${API}/policies`, config)
      ]);

      setEmployees(employeesRes.data);
      setManagers(employeesRes.data.filter(e => e.role === 'manager' || e.role === 'admin'));
      setHolidays(holidaysRes.data);
      setPolicies(policiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };















  const handleToggleEmployeeStatus = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API}/users/${userId}/toggle-status`, {}, config);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle employee status'));
    }
  };







  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingEmployee) {
        const updateData = { ...employeeForm };
        delete updateData.employee_id;
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/users/${editingEmployee.id}`, updateData, config);
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API}/users`, employeeForm, config);
        toast.success('Employee created successfully');
      }

      setShowEmployeeDialog(false);
      setEditingEmployee(null);
      setEmployeeForm({
        employee_id: '',
        name: '',
        email: '',
        password: '',
        role: 'employee',
        manager_id: '',
        date_of_joining: '',
        department: '',
        designation: '',
        gender: '',
        dob: '',
        marital_status: '',
        nationality: '',
        present_address: '',
        permanent_address: '',
        contact_number: '',
        alternate_number: '',
        alternate_email: '',
        education: '',
        previous_employment: '',
        employee_type: '',
        level: '',
        reporting_manager: '',
        rm_email: '',
        hr_partner: '',
        hr_email: '',
        converted_employee: false,
        conversion_date: '',
        actual_date_of_joining: '',
        company_asset: '',
        serial_number: '',
        total_work_experience: '',
        work_experience_as_employee: '',
        pan_number: '',
        aadhar_number: '',
        bank_details: '',
        project_name: '',
        last_promotion: '',
        contract_end_date: '',
        contract_period: '',
        internship_end_date: '',
        internship_period: '',
        profile_photo: '',
        payslip_folder_id: '',
        salary_currency: '',
        fixed_monthly_salary: '',
        internet_phone_allowance: '',
        monthly_performance_bonus: '',
        byod_allowance: '',
        half_yearly_retention_bonus: '',
        managerial_allowance: '',
        provident_fund: '',
        medical_allowance: '',
        gratuity: '',
        hra: '',
        ctc_yearly: ''
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/users/${id}`, config);
      toast.success('Employee deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  // Excel Download/Upload Handlers
  const handleDownloadEmployeeData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emp-tab/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employee_data_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Employee data downloaded successfully');
    } catch (error) {
      toast.error('Failed to download employee data');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/emp-tab/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_bulk_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/emp-tab/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data.results);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploading(false);
    }
  };

  const handleEditEmployee = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch complete employee details
      const response = await axios.get(`${API}/users/${employee.id}/details`, config);
      const details = response.data;
      
      setEditingEmployee(employee);
      setEmployeeForm({
        employee_id: details.employee_id || '',
        name: details.name || '',
        email: details.email || '',
        password: '',
        role: details.role || 'employee',
        manager_id: details.manager_id || '',
        date_of_joining: formatDateForInput(details.date_of_joining),
        department: details.department || '',
        designation: details.designation || '',
        gender: details.gender || '',
        dob: formatDateForInput(details.dob),
        marital_status: details.marital_status || '',
        nationality: details.nationality || '',
        present_address: details.present_address || '',
        permanent_address: details.permanent_address || '',
        contact_number: String(details.contact_number || ''),
        alternate_number: String(details.alternate_number || ''),
        alternate_email: details.alternate_email || '',
        education: details.education || '',
        previous_employment: details.previous_employment || '',
        employee_type: details.employee_type || '',
        level: details.level || '',
        reporting_manager: details.reporting_manager || '',
        rm_email: details.rm_email || '',
        hr_partner: details.hr_partner || '',
        hr_email: details.hr_email || '',
        converted_employee: details.converted_employee || false,
        conversion_date: formatDateForInput(details.conversion_date),
        actual_date_of_joining: formatDateForInput(details.actual_date_of_joining),
        company_asset: details.company_asset || '',
        serial_number: details.serial_number || '',
        total_work_experience: details.total_work_experience || '',
        work_experience_as_employee: details.work_experience_as_employee || '',
        pan_number: details.pan_number || '',
        aadhar_number: details.aadhar_number || '',
        bank_details: details.bank_details || '',
        project_name: details.project_name || '',
        last_promotion: formatDateForInput(details.last_promotion),
        contract_end_date: formatDateForInput(details.contract_end_date),
        contract_period: details.contract_period || '',
        internship_end_date: formatDateForInput(details.internship_end_date),
        internship_period: details.internship_period || '',
        profile_photo: details.profile_photo || '',
        payslip_folder_id: details.payslip_folder_id || '',
        salary_currency: details.salary_currency || '',
        fixed_monthly_salary: details.fixed_monthly_salary || '',
        internet_phone_allowance: details.internet_phone_allowance || '',
        monthly_performance_bonus: details.monthly_performance_bonus || '',
        byod_allowance: details.byod_allowance || '',
        half_yearly_retention_bonus: details.half_yearly_retention_bonus || '',
        managerial_allowance: details.managerial_allowance || '',
        provident_fund: details.provident_fund || '',
        medical_allowance: details.medical_allowance || '',
        gratuity: details.gratuity || '',
        hra: details.hra || '',
        ctc_yearly: details.ctc_yearly || '',
        active_date: details.active_date || '',
        inactive_date: details.inactive_date || ''
      });
      setShowEmployeeDialog(true);
    } catch (error) {
      toast.error('Failed to load employee details');
    }
  };

  const handleCreateHoliday = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingHoliday) {
        // Update existing holiday
        await axios.put(`${API}/holidays/${editingHoliday.id}`, holidayForm, config);
        toast.success('Holiday updated successfully');
      } else {
        // Create new holiday
        await axios.post(`${API}/holidays`, holidayForm, config);
        toast.success('Holiday created successfully');
      }
      
      setShowHolidayDialog(false);
      setEditingHoliday(null);
      setHolidayForm({ date: '', name: '', holiday_type: 'organization' });
      fetchData();
    } catch (error) {
      toast.error(editingHoliday ? 'Failed to update holiday' : 'Failed to create holiday');
    }
  };
  
  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      date: holiday.date,
      name: holiday.name,
      holiday_type: holiday.holiday_type
    });
    setShowHolidayDialog(true);
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/holidays/${id}`, config);
      toast.success('Holiday deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingPolicy) {
        await axios.put(`${API}/policies/${editingPolicy.id}`, policyForm, config);
        toast.success('Policy updated successfully');
        setEditingPolicy(null);
      } else {
        await axios.post(`${API}/policies`, policyForm, config);
        toast.success('Policy created successfully');
      }
      
      setShowPolicyDialog(false);
      setPolicyForm({ title: '', drive_file_id: '' });
      fetchData();
    } catch (error) {
      toast.error(editingPolicy ? 'Failed to update policy' : 'Failed to create policy');
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      title: policy.title,
      drive_file_id: policy.drive_file_id
    });
    setShowPolicyDialog(true);
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/policies/${id}`, config);
      toast.success('Policy deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50" data-testid="admin-dashboard">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-gray-600 text-lg">Manage employees, holidays, and policies</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-1 mb-6 border-b" data-testid="admin-tab-nav">
          <button
            onClick={() => setActiveTab('employees')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'employees' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-employees"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Employees
          </button>
          <button
            onClick={() => setActiveTab('holidays')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'holidays' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-holidays"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Holidays
          </button>
          <button
            onClick={() => setActiveTab('policies')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'policies' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-policies"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Policies
          </button>
          <button
            onClick={() => setActiveTab('kpp')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'kpp' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-kpp"
          >
            <Target className="w-4 h-4 inline mr-2" />
            Stack Ranking KPP
          </button>
          <button
            onClick={() => setActiveTab('appraisal-kpp')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'appraisal-kpp' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-appraisal-kpp"
          >
            <ClipboardCheck className="w-4 h-4 inline mr-2" />
            Appraisal KPP
          </button>
          <button
            onClick={() => setActiveTab('appraisal-reports')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'appraisal-reports' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-appraisal-reports"
          >
            <ClipboardList className="w-4 h-4 inline mr-2" />
            Appraisal Reports
          </button>
          <button
            onClick={() => setActiveTab('compensation')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'compensation' ? 'border-b-2 border-teal-600 text-teal-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-compensation"
          >
            <HandCoins className="w-4 h-4 inline mr-2" />
            Compensation
          </button>
          <button
            onClick={() => setActiveTab('deadline-monitor')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'deadline-monitor' ? 'border-b-2 border-orange-600 text-orange-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-deadline-monitor"
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Deadlines
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'analytics' ? 'border-b-2 border-cyan-600 text-cyan-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('assets')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'assets' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-assets"
          >
            <Package className="w-4 h-4 inline mr-2" />
            Assets
          </button>
          <button
            onClick={() => setActiveTab('training')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'training' ? 'border-b-2 border-teal-600 text-teal-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-training"
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Training
          </button>
          <button
            onClick={() => setActiveTab('hr-portal')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'hr-portal' ? 'border-b-2 border-cyan-600 text-cyan-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-hr-portal"
          >
            <Eye className="w-4 h-4 inline mr-2" />
            HR Portal
          </button>
          <button
            onClick={() => setActiveTab('hiring')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'hiring' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-hiring"
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Hiring
          </button>
          <button
            onClick={() => setActiveTab('pnl')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'pnl' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-pnl"
          >
            <IndianRupee className="w-4 h-4 inline mr-2" />
            P&L
          </button>
          <button
            onClick={() => setActiveTab('payroll')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'payroll' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-payroll"
          >
            <Wallet className="w-4 h-4 inline mr-2" />
            Payroll
          </button>
          <button
            onClick={() => setActiveTab('portal-access')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'portal-access' ? 'border-b-2 border-amber-600 text-amber-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-portal-access"
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Portal Access
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'security' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-security"
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('releases')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'releases' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-releases"
          >
            <Rocket className="w-4 h-4 inline mr-2" />
            Releases
          </button>
          <button
            onClick={() => setActiveTab('manager-transfer')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'manager-transfer' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-manager-transfer"
          >
            <ArrowRightLeft className="w-4 h-4 inline mr-2" />
            Manager Transfer
          </button>
          <button
            onClick={() => setActiveTab('apm')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'apm' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-apm"
          >
            <Activity className="w-4 h-4 inline mr-2" />
            APM
          </button>
          <button
            onClick={() => setActiveTab('banners')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'banners' ? 'border-b-2 border-amber-600 text-amber-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-banners"
          >
            <Megaphone className="w-4 h-4 inline mr-2" />
            Banners
          </button>
          <button
            onClick={() => setActiveTab('team-attendance')}
            className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'team-attendance' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="tab-team-attendance"
          >
            <CalendarDays className="w-4 h-4 inline mr-2" />
            Attendance View
          </button>
          {user?.role === 'superadmin' && (
            <button
              onClick={() => setActiveTab('superadmin')}
              className={`px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm ${activeTab === 'superadmin' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="tab-superadmin"
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Super Admin
            </button>
          )}
        </div>

        {/* Super Admin Portal Tab */}
        {activeTab === 'superadmin' && user?.role === 'superadmin' && (
          <SuperAdminPortal user={user} />
        )}

        {/* P&L Portal Tab */}
        {activeTab === 'pnl' && (
          <PnLPortal user={user} />
        )}

        {/* Payroll Portal Tab */}
        {activeTab === 'payroll' && (
          <PayrollPortal user={user} />
        )}

        {/* Employee Analytics Tab */}
        {activeTab === 'analytics' && (
          <EmployeeAnalytics />
        )}

        {/* Asset Management Tab */}
        {activeTab === 'assets' && (
          <AssetManagement />
        )}

        {/* Training Management Tab */}
        {activeTab === 'training' && (
          <TrainingManagement token={localStorage.getItem('token')} />
        )}

        {/* HR Portal Tab */}
        {activeTab === 'hr-portal' && (
          <HRPortal user={user} />
        )}

        {/* Hiring Portal Tab */}
        {activeTab === 'hiring' && (
          <HiringPortal user={user} />
        )}

        {/* Portal Access Management Tab */}
        {activeTab === 'portal-access' && (
          <PortalAccessManager token={localStorage.getItem('token')} />
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <SecuritySettings />
        )}

        {/* Releases Tab */}
        {activeTab === 'releases' && (
          <VersionPublisher />
        )}

        {/* Manager Transfer Tab */}
        {activeTab === 'manager-transfer' && (
          <ManagerHistoryTransfer />
        )}

        {/* APM Tab */}
        {activeTab === 'apm' && (
          <APMPortal />
        )}

        {/* Banners Tab */}
        {activeTab === 'banners' && (
          <BannerManager />
        )}

        {/* Team Attendance View — org-wide (admin picks any employees) */}
        {activeTab === 'team-attendance' && (
          <TeamAttendanceView user={user} />
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <Card data-testid="employees-section">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <CardTitle>Employee Management</CardTitle>
              <div className="flex flex-wrap gap-2">
                {/* Download Data Button */}
                <Button variant="outline" onClick={handleDownloadEmployeeData} data-testid="download-employee-data">
                  <Download className="w-4 h-4 mr-2" />
                  Download Data
                </Button>
                
                {/* Download Template Button */}
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
                
                {/* Bulk Upload Dialog */}
                <Dialog open={showBulkUploadDialog} onOpenChange={(open) => {
                  setShowBulkUploadDialog(open);
                  if (!open) {
                    setUploadFile(null);
                    setUploadResult(null);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50" data-testid="bulk-upload-button">
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Bulk Upload Employees</DialogTitle>
                      <CardDescription>
                        Upload an Excel file to add multiple employees at once
                      </CardDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                          className="hidden"
                          id="bulk-upload-input"
                          data-testid="bulk-upload-input"
                        />
                        <label htmlFor="bulk-upload-input" className="cursor-pointer">
                          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                          <p className="text-sm text-gray-600">
                            {uploadFile ? uploadFile.name : 'Click to select Excel file (.xlsx)'}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            Download template first for correct format
                          </p>
                        </label>
                      </div>
                      
                      {uploadResult && (
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {uploadResult.created.length > 0 && (
                            <div className="bg-green-50 p-3 rounded-lg">
                              <p className="font-medium text-green-700 mb-2">
                                ✓ Created: {uploadResult.created.length} employees
                              </p>
                              <div className="text-sm text-green-600 space-y-1">
                                {uploadResult.created.slice(0, 5).map((c, i) => (
                                  <p key={i}>Row {c.row}: {c.name} ({c.employee_id})</p>
                                ))}
                                {uploadResult.created.length > 5 && (
                                  <p>...and {uploadResult.created.length - 5} more</p>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {uploadResult.skipped.length > 0 && (
                            <div className="bg-yellow-50 p-3 rounded-lg">
                              <p className="font-medium text-yellow-700 mb-2">
                                ⚠ Skipped: {uploadResult.skipped.length} rows
                              </p>
                              <div className="text-sm text-yellow-600 space-y-1">
                                {uploadResult.skipped.map((s, i) => (
                                  <p key={i}>Row {s.row}: {s.employee_id} - {s.reason}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {uploadResult.errors.length > 0 && (
                            <div className="bg-red-50 p-3 rounded-lg">
                              <p className="font-medium text-red-700 mb-2">
                                ✗ Errors: {uploadResult.errors.length} rows
                              </p>
                              <div className="text-sm text-red-600 space-y-1">
                                {uploadResult.errors.map((e, i) => (
                                  <p key={i}>Row {e.row}: {e.error}</p>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={handleDownloadTemplate}
                          className="flex-1"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Download Template
                        </Button>
                        <Button 
                          onClick={handleBulkUpload}
                          disabled={!uploadFile || uploading}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          data-testid="confirm-bulk-upload"
                        >
                          {uploading ? 'Uploading...' : 'Upload & Create Employees'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Add Employee Dialog */}
                <Dialog open={showEmployeeDialog} onOpenChange={(open) => {
                  setShowEmployeeDialog(open);
                  if (!open) {
                    setEditingEmployee(null);
                    setEmployeeForm({
                      employee_id: '',
                      name: '',
                      email: '',
                      password: '',
                      role: 'employee',
                      manager_id: '',
                      date_of_joining: '',
                      department: '',
                      designation: ''
                    });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-employee-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Employee
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Create New Employee'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateEmployee} className="space-y-4" data-testid="employee-form">
                      <EmployeeFormFields 
                        employeeForm={employeeForm}
                        setEmployeeForm={setEmployeeForm}
                        managers={managers}
                        editingEmployee={editingEmployee}
                      />
                      <Button type="submit" className="w-full" data-testid="save-employee-button">
                        {editingEmployee ? 'Update Employee' : 'Create Employee'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search & Column Visibility Controls */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, ID, email, department..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="employee-search-input"
                  />
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 gap-2" data-testid="column-toggle-button">
                      <SlidersHorizontal className="w-4 h-4" />
                      Columns
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-52 p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Toggle Columns</p>
                    <div className="space-y-2">
                      {columnDefs.map(col => (
                        <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={visibleColumns[col.key]}
                            onCheckedChange={(checked) =>
                              setVisibleColumns(prev => ({ ...prev, [col.key]: !!checked }))
                            }
                            data-testid={`column-toggle-${col.key}`}
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <span className="text-sm text-gray-500 ml-auto">
                  {filteredEmployees.length} of {employees.length} employees
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.employee_id && <TableHead>Employee ID</TableHead>}
                    {visibleColumns.name && <TableHead>Name</TableHead>}
                    {visibleColumns.email && <TableHead>Email</TableHead>}
                    {visibleColumns.role && <TableHead>Role</TableHead>}
                    {visibleColumns.department && <TableHead>Department</TableHead>}
                    {visibleColumns.designation && <TableHead>Designation</TableHead>}
                    {visibleColumns.date_of_joining && <TableHead>Date of Joining</TableHead>}
                    {visibleColumns.employee_type && <TableHead>Employee Type</TableHead>}
                    {visibleColumns.level && <TableHead>Level</TableHead>}
                    {visibleColumns.contract_end_date && <TableHead>Contract End Date</TableHead>}
                    {visibleColumns.internship_end_date && <TableHead>Internship End Date</TableHead>}
                    {visibleColumns.status && <TableHead className="text-center">Status</TableHead>}
                    {visibleColumns.actions && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map(emp => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.employee_id}`} className={emp.is_active === false ? 'bg-gray-50 opacity-70' : ''}>
                      {visibleColumns.employee_id && <TableCell>{emp.employee_id}</TableCell>}
                      {visibleColumns.name && <TableCell>{emp.name}</TableCell>}
                      {visibleColumns.email && <TableCell>{emp.email}</TableCell>}
                      {visibleColumns.role && <TableCell><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">{emp.role}</span></TableCell>}
                      {visibleColumns.department && <TableCell>{emp.department || '-'}</TableCell>}
                      {visibleColumns.designation && <TableCell>{emp.designation || '-'}</TableCell>}
                      {visibleColumns.date_of_joining && <TableCell>{emp.date_of_joining || '-'}</TableCell>}
                      {visibleColumns.employee_type && <TableCell>{emp.employee_type || '-'}</TableCell>}
                      {visibleColumns.level && <TableCell>{emp.level || '-'}</TableCell>}
                      {visibleColumns.contract_end_date && <TableCell data-testid={`contract-end-date-${emp.employee_id}`}>{emp.contract_end_date || '-'}</TableCell>}
                      {visibleColumns.internship_end_date && <TableCell data-testid={`internship-end-date-${emp.employee_id}`}>{emp.internship_end_date || '-'}</TableCell>}
                      {visibleColumns.status && (
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Switch 
                              checked={emp.is_active !== false}
                              onCheckedChange={() => handleToggleEmployeeStatus(emp.id)}
                              disabled={emp.role === 'admin' && emp.id === user?.id}
                              data-testid={`toggle-employee-${emp.employee_id}`}
                            />
                            <span className={`text-xs font-medium ${emp.is_active !== false ? 'text-green-600' : 'text-gray-400'}`}>
                              {emp.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.actions && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEditEmployee(emp)} data-testid={`edit-employee-${emp.employee_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEmployee(emp.id)} className="text-red-600" data-testid={`delete-employee-${emp.employee_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Holidays Tab */}
        {activeTab === 'holidays' && (
          <Card data-testid="holidays-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Holiday Management</CardTitle>
              <Dialog open={showHolidayDialog} onOpenChange={(open) => {
                setShowHolidayDialog(open);
                if (!open) {
                  setEditingHoliday(null);
                  setHolidayForm({ date: '', name: '', holiday_type: 'organization' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-holiday-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Holiday
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Create New Holiday'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateHoliday} className="space-y-4" data-testid="holiday-form">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={holidayForm.date}
                        onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                        required
                        data-testid="holiday-date-input"
                      />
                    </div>
                    <div>
                      <Label>Holiday Name</Label>
                      <Input
                        value={holidayForm.name}
                        onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                        required
                        data-testid="holiday-name-input"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={holidayForm.holiday_type} onValueChange={(value) => setHolidayForm({ ...holidayForm, holiday_type: value })}>
                        <SelectTrigger data-testid="holiday-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="organization">Organization Holiday</SelectItem>
                          <SelectItem value="restricted">Restricted Holiday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full" data-testid="save-holiday-button">
                      {editingHoliday ? 'Update Holiday' : 'Create Holiday'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Holiday Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.map(holiday => (
                    <TableRow key={holiday.id} data-testid={`holiday-row-${holiday.id}`}>
                      <TableCell>{holiday.date}</TableCell>
                      <TableCell>{holiday.name}</TableCell>
                      <TableCell><span className={`px-2 py-1 ${holiday.holiday_type === 'organization' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'} rounded-full text-xs`}>{holiday.holiday_type}</span></TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEditHoliday(holiday)} className="text-blue-600" data-testid={`edit-holiday-${holiday.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(holiday.id)} className="text-red-600" data-testid={`delete-holiday-${holiday.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <Card data-testid="policies-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Policy Management</CardTitle>
              <Dialog open={showPolicyDialog} onOpenChange={(open) => {
                setShowPolicyDialog(open);
                if (!open) {
                  setEditingPolicy(null);
                  setPolicyForm({ title: '', drive_file_id: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-policy-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Policy
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePolicy} className="space-y-4" data-testid="policy-form">
                    <div>
                      <Label>Policy Title</Label>
                      <Input
                        value={policyForm.title}
                        onChange={(e) => setPolicyForm({ ...policyForm, title: e.target.value })}
                        required
                        data-testid="policy-title-input"
                      />
                    </div>
                    <div>
                      <Label>Google Drive File ID</Label>
                      <Input
                        value={policyForm.drive_file_id}
                        onChange={(e) => setPolicyForm({ ...policyForm, drive_file_id: e.target.value })}
                        required
                        placeholder="e.g., 1a2B3c4D5e6F7g8H9i0J"
                        data-testid="policy-drive-id-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Extract from Google Drive share link</p>
                    </div>
                    <Button type="submit" className="w-full" data-testid="save-policy-button">
                      {editingPolicy ? 'Update Policy' : 'Create Policy'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy Title</TableHead>
                    <TableHead>Drive File ID</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {policies.map(policy => (
                    <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                      <TableCell>{policy.title}</TableCell>
                      <TableCell className="font-mono text-sm">{policy.drive_file_id}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditPolicy(policy)} className="text-blue-600" data-testid={`edit-policy-${policy.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePolicy(policy.id)} className="text-red-600" data-testid={`delete-policy-${policy.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* KPP Parameters Tab */}
        {activeTab === 'kpp' && <KPPManagement />}

        {/* Appraisal KPP Tab */}
        {activeTab === 'appraisal-kpp' && <AppraisalKPPManagement />}

        {/* Appraisal Reports Tab */}
        {activeTab === 'appraisal-reports' && <AppraisalReportsAdmin />}

        {/* Compensation Tab */}
        {activeTab === 'compensation' && <CompensationManagement />}

        {/* Deadline Monitor Tab */}
        {activeTab === 'deadline-monitor' && <DeadlineMonitor />}

      </div>
    </div>
  );
};

export default AdminDashboard;
